<script setup lang="ts">
/**
 * Config-driven vertical landing page template.
 *
 * One template, six configurations — the conversion-rate optimisation
 * benefit of fixed layout, the marketing benefit of vertical-specific copy.
 *
 * Pass props from each /pages/{vertical}.vue:
 *   - vertical          slug (used in SEO + URLs)
 *   - tone              one of "warm" | "professional" | "modern" | "minimal"
 *   - hero              { eyebrow, headline (HTML), subhead, cta_text }
 *   - features          array of { icon, title, description }
 *   - workflow_steps    array of { step, title, description } (3 ideal)
 *   - pain_points       array of strings ("you've been juggling…")
 *   - relief_points     array of strings ("now you…")
 *   - sample_screen     { title, items: [{ label, value }] }  — mock case card
 *   - faq               array of { q, a }
 *   - pricing_callout   string
 */
interface HeroBlock {
  eyebrow: string
  headline: string
  subhead: string
  cta_text?: string
}
interface Feature { icon: string; title: string; description: string }
interface WorkflowStep { step: string; title: string; description: string }
interface SampleScreen { title: string; subtitle?: string; items: Array<{ label: string; value: string }> }
interface FAQ { q: string; a: string }

const props = defineProps<{
  vertical: string
  hero: HeroBlock
  features: Feature[]
  workflow_steps: WorkflowStep[]
  pain_points: string[]
  relief_points: string[]
  sample_screen: SampleScreen
  faq: FAQ[]
  pricing_callout: string
  accent_from?: string
  accent_to?: string
  /** Not currently sold: noindex the page and point the CTA at contact instead of signup. */
  unlisted?: boolean
}>()

const accentFrom = computed(() => props.accent_from ?? 'from-indigo-500')
const accentTo   = computed(() => props.accent_to   ?? 'to-purple-500')

// Related blog posts for this vertical, pulled from the shared manifest.
const relatedBlogPosts = computed(() => {
  // Auto-imported from utils/blogPosts.ts
  return postsForVertical(props.vertical as any)
})

// Same-vertical testimonials (auto-imported from utils/testimonials.ts)
const verticalTestimonials = computed(() =>
  testimonialsForVertical(props.vertical as any)
)
const verticalLabel = computed(() => props.vertical.replace('-', ' '))

// Fire a single funnel event when the vertical page is viewed.
const analytics = useAnalytics()
onMounted(() => {
  analytics.track('Vertical Viewed', { vertical: props.vertical })
})

useSeoMeta({
  robots: props.unlisted ? 'noindex, nofollow' : undefined,
  title: `Caseflow for ${props.vertical.replace('-', ' ')}`,
  description: props.hero.subhead,
  ogTitle: `Caseflow for ${props.vertical.replace('-', ' ')}`,
  ogDescription: props.hero.subhead,
  ogImage: '/og-image.png',
  ogType: 'website',
  twitterCard: 'summary_large_image',
  twitterImage: '/og-image.png',
})

// Tell Google this is a SaaS product. Helps the search snippet render with
// "AUD $39 - $249" pricing hint and an "Application" category.
useStructuredData({
  '@type': 'SoftwareApplication',
  name: `Caseflow for ${props.vertical.replace('-', ' ')}`,
  applicationCategory: 'BusinessApplication',
  operatingSystem: 'Web',
  description: props.hero.subhead,
  offers: {
    '@type': 'AggregateOffer',
    priceCurrency: 'AUD',
    lowPrice: '39',
    highPrice: '249',
    offerCount: 3,
  },
  provider: { '@type': 'Organization', name: 'Caseflow' },
})
</script>

<template>
  <!-- ── HERO ─────────────────────────────────────────────────── -->
  <section class="relative overflow-hidden">
    <div :class="['absolute inset-0 -z-10 bg-gradient-to-br', accentFrom, accentTo, 'opacity-10']"></div>
    <div class="mx-auto max-w-7xl px-6 pt-20 pb-24 grid lg:grid-cols-2 gap-12 items-center">
      <div>
        <p :class="['inline-block text-xs uppercase tracking-wider font-medium px-3 py-1 rounded-full bg-gradient-to-r', accentFrom, accentTo, 'text-white']">
          {{ hero.eyebrow }}
        </p>
        <h1 class="mt-6 text-4xl md:text-5xl font-bold tracking-tight text-gray-900" v-html="hero.headline" />
        <p class="mt-6 text-lg text-gray-600 leading-relaxed max-w-xl">{{ hero.subhead }}</p>
        <div class="mt-8 flex flex-wrap gap-3">
          <NuxtLink :to="unlisted ? '/contact' : '/signup'" :class="['inline-block rounded-lg px-5 py-3 text-sm font-medium text-white bg-gradient-to-br', accentFrom, accentTo, 'hover:opacity-95']">
            {{ unlisted ? 'Register interest' : (hero.cta_text ?? 'Start 14-day free trial') }}
          </NuxtLink>
          <NuxtLink to="/pricing" class="inline-block rounded-lg border border-gray-300 px-5 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50">See pricing</NuxtLink>
        </div>
        <p class="mt-4 text-xs text-gray-500">No credit card · cancel anytime · imports from your Excel sheet on day one</p>
      </div>

      <!-- Mocked-up case card to give the page a product feel without a screenshot -->
      <div class="relative">
        <div :class="['absolute -inset-3 rounded-3xl bg-gradient-to-br opacity-20 blur-2xl', accentFrom, accentTo]"></div>
        <div class="relative rounded-2xl border border-gray-200 bg-white shadow-xl p-6">
          <div class="flex items-start justify-between">
            <div>
              <p class="text-xs text-gray-500">{{ sample_screen.subtitle ?? 'Sample case' }}</p>
              <h3 class="text-lg font-semibold text-gray-900">{{ sample_screen.title }}</h3>
            </div>
            <span :class="['rounded px-2 py-0.5 text-[10px] uppercase font-medium text-white bg-gradient-to-br', accentFrom, accentTo]">Active</span>
          </div>
          <dl class="mt-5 space-y-2 text-sm">
            <div v-for="item in sample_screen.items" :key="item.label" class="flex justify-between border-b border-gray-100 last:border-b-0 py-1.5">
              <dt class="text-gray-500">{{ item.label }}</dt>
              <dd class="text-gray-900 font-medium">{{ item.value }}</dd>
            </div>
          </dl>
          <div class="mt-5 flex gap-2 text-xs">
            <span class="rounded bg-emerald-100 text-emerald-700 px-2 py-0.5">3 docs ✓</span>
            <span class="rounded bg-amber-100 text-amber-700 px-2 py-0.5">Deposit pending</span>
            <span class="rounded bg-indigo-100 text-indigo-700 px-2 py-0.5">VIP</span>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- ── PAIN / RELIEF — "what you have vs what you'd have" ─── -->
  <section class="mx-auto max-w-5xl px-6 py-16">
    <div class="grid md:grid-cols-2 gap-6">
      <div class="rounded-xl border border-red-100 bg-red-50/50 p-6">
        <p class="text-xs uppercase tracking-wider font-medium text-red-700">Right now</p>
        <ul class="mt-4 space-y-3 text-sm text-gray-800">
          <li v-for="p in pain_points" :key="p" class="flex gap-2">
            <span class="text-red-500">✕</span>{{ p }}
          </li>
        </ul>
      </div>
      <div class="rounded-xl border border-emerald-100 bg-emerald-50/50 p-6">
        <p class="text-xs uppercase tracking-wider font-medium text-emerald-700">With Caseflow</p>
        <ul class="mt-4 space-y-3 text-sm text-gray-800">
          <li v-for="r in relief_points" :key="r" class="flex gap-2">
            <span class="text-emerald-600">✓</span>{{ r }}
          </li>
        </ul>
      </div>
    </div>
  </section>

  <!-- ── FEATURE GRID ─────────────────────────────────────────── -->
  <section class="mx-auto max-w-7xl px-6 py-20">
    <h2 class="text-3xl font-bold tracking-tight text-gray-900">Built for the way you actually work</h2>
    <p class="mt-3 text-gray-600 max-w-2xl">Six features designed for your vertical out of the box — no configuration weekend required.</p>
    <div class="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
      <div v-for="f in features" :key="f.title" class="rounded-xl border border-gray-200 bg-white p-6 hover:shadow-md transition">
        <div :class="['w-10 h-10 rounded-lg bg-gradient-to-br flex items-center justify-center text-xl', accentFrom, accentTo, 'text-white']">{{ f.icon }}</div>
        <h3 class="mt-4 font-semibold text-gray-900">{{ f.title }}</h3>
        <p class="mt-2 text-sm text-gray-600">{{ f.description }}</p>
      </div>
    </div>
  </section>

  <!-- ── HOW IT WORKS ─────────────────────────────────────────── -->
  <section class="bg-gray-50 py-20">
    <div class="mx-auto max-w-7xl px-6">
      <h2 class="text-3xl font-bold tracking-tight text-gray-900">How it works</h2>
      <div class="mt-10 grid md:grid-cols-3 gap-6">
        <div v-for="s in workflow_steps" :key="s.step" class="rounded-xl bg-white p-6 border border-gray-200">
          <p :class="['text-xs font-medium tracking-wider uppercase bg-clip-text text-transparent bg-gradient-to-r', accentFrom, accentTo]">Step {{ s.step }}</p>
          <h3 class="mt-2 text-lg font-semibold text-gray-900">{{ s.title }}</h3>
          <p class="mt-2 text-sm text-gray-600">{{ s.description }}</p>
        </div>
      </div>
    </div>
  </section>

  <!-- ── FAQ ──────────────────────────────────────────────────── -->
  <section class="mx-auto max-w-3xl px-6 py-20">
    <h2 class="text-3xl font-bold tracking-tight text-gray-900">Frequently asked</h2>
    <div class="mt-8 divide-y divide-gray-100 border-y border-gray-100">
      <details v-for="(item, i) in faq" :key="i" class="group py-5">
        <summary class="cursor-pointer flex justify-between items-center text-base font-medium text-gray-900 list-none">
          {{ item.q }}
          <span class="ml-4 text-gray-400 group-open:rotate-180 transition">▾</span>
        </summary>
        <p class="mt-3 text-sm text-gray-600 leading-relaxed">{{ item.a }}</p>
      </details>
    </div>
  </section>

  <!-- ── TESTIMONIALS (same-vertical) ─────────────────────────── -->
  <TestimonialGrid
    v-if="verticalTestimonials.length"
    :testimonials="verticalTestimonials"
    :heading="`What ${verticalLabel} say`"
    :subheading="`Real talk from agencies already running on Caseflow.`"
    background="white"
  />

  <!-- ── RELATED READING ──────────────────────────────────────── -->
  <section v-if="relatedBlogPosts.length" class="bg-gray-50 py-16">
    <div class="mx-auto max-w-5xl px-6">
      <header class="flex items-baseline justify-between flex-wrap gap-2">
        <h2 class="text-2xl font-semibold text-gray-900">From the blog</h2>
        <NuxtLink to="/blog" class="text-sm text-indigo-600 hover:underline">All posts →</NuxtLink>
      </header>
      <div class="mt-6 grid md:grid-cols-2 gap-4">
        <NuxtLink
          v-for="post in relatedBlogPosts" :key="post.slug"
          :to="`/blog/${post.slug}`"
          class="block rounded-xl border border-gray-200 bg-white p-5 hover:shadow-md transition"
        >
          <div :class="['h-1 w-12 rounded mb-4 bg-gradient-to-r', accentFrom, accentTo]"></div>
          <h3 class="font-semibold text-gray-900">{{ post.title }}</h3>
          <p class="mt-2 text-sm text-gray-600">{{ post.excerpt }}</p>
          <p class="mt-4 text-xs text-gray-500">{{ post.read_minutes }} min read</p>
        </NuxtLink>
      </div>
    </div>
  </section>

  <!-- ── PRICING CALLOUT + CTA ────────────────────────────────── -->
  <section :class="['py-20 bg-gradient-to-br', accentFrom, accentTo, 'text-white']">
    <div class="mx-auto max-w-4xl px-6 text-center">
      <h2 class="text-3xl font-bold">{{ pricing_callout }}</h2>
      <p class="mt-3 text-white/80">Start free. Import your existing client list in 5 minutes. Cancel anytime.</p>
      <div class="mt-8 flex justify-center gap-3 flex-wrap">
        <NuxtLink to="/signup" class="rounded-lg bg-white text-gray-900 px-6 py-3 text-sm font-medium hover:bg-gray-100">Start 14-day free trial</NuxtLink>
        <NuxtLink to="/pricing" class="rounded-lg border border-white/40 text-white px-6 py-3 text-sm font-medium hover:bg-white/10">See pricing</NuxtLink>
      </div>
    </div>
  </section>
</template>
